import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { connect } from 'react-redux';
import GridLayout from 'react-grid-layout';
import { useIntl } from 'react-intl';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { setUserSetting } from '../../../actions/Auth';

import { APIupdateUserSettings } from '../../../api/User';

import DashboardPersonalizeElement from './PersonalizeElement';
import ProcessingSpinner from '../../ProcessingSpinner';

import { getDashboardAvailableComponents } from '../../../helpers/Dashboard';
import { getOnboardingSteps } from '../../../helpers/Onboarding';
import { getUserSetting } from '../../../helpers/Settings';

import ErrorBoundary from '../../ErrorBoundary';

const DashboardPersonalizeModal = ({
  profile,
  updateUserSetting,
  handleClose,
}) => {
  const intl = useIntl();
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLayout, setNewLayout] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(!getUserSetting('onboarding_dismissed'));

  const availableComponents = getDashboardAvailableComponents();
  const availableComponentsSlugs = availableComponents.map(c => c.slug);
  const userOrder = getUserSetting('dashboard_widgets') || [];
  const onboardingSteps = getOnboardingSteps();

  const userLayout = userOrder
    .filter(c => availableComponentsSlugs.includes(c.slug))
    .map((c) => {
      const original = availableComponents.find(a => a.slug === c.slug);

      return {
        i: c.slug,
        minW: original.min_width || 1,
        maxH: 3,
        x: c.x || 0,
        y: c.y || availableComponents.length + 1,
        w: c.width || c.default_width || 12,
        h: c.height || c.default_height || 1,
        ...c,
      };
    });

  const userLayoutSlugs = userLayout.map(c => c.slug);

  const unusedElementsLayout = availableComponents
    .filter(available => !userLayoutSlugs.includes(available.slug))
    .map((c, i) => ({
      i: c.slug,
      x: 0,
      y: userLayout.length + i,
      w: c.default_width || 12,
      h: c.default_height || 1,
      minW: c.min_width || 2,
      maxH: 3,
      ...c,
    }));

  const onLayoutChange = (changedLayout) => {
    setNewLayout(changedLayout);
  };

  useEffect(() => {
    setSelectedComponents(
      [
        ...userLayout.filter(c => c.selected).map(c => c.i),
        ...unusedElementsLayout.map(c => c.i),
      ],
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeComponentSelected = (slug) => {
    setSelectedComponents(prev => (
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    ));
  };

  const layout = [...userLayout, ...unusedElementsLayout]
    .filter(c => selectedComponents.includes(c.slug));

  const handleSave = async () => {
    setLoading(true);

    const selectedComponentsValue = newLayout.map(c => ({
      slug: c.i,
      x: c.x,
      y: c.y,
      w: c.w,
      h: c.h,
      selected: true,
    }));

    const unselectedComponentsValue = availableComponents
      .filter(a => !selectedComponents.includes(a.slug))
      .map(c => ({ slug: c.slug, selected: false }));

    const settingValue = [...selectedComponentsValue, ...unselectedComponentsValue];

    const settingsToUpdate = {
      dashboard_widgets: settingValue,
    };

    if (!getUserSetting('onboarding_dismissed') !== showOnboarding) {
      settingsToUpdate.onboarding_dismissed = !showOnboarding;
    }

    await APIupdateUserSettings(profile.uuid, settingsToUpdate);
    updateUserSetting(settingsToUpdate);

    handleClose();
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
        const innerWidth = containerRef.current.clientWidth - paddingLeft - paddingRight;
        const innerHeight = containerRef.current.clientHeight - paddingTop - paddingBottom;
        setWidth(innerWidth);
        setHeight(innerHeight);
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <ErrorBoundary>
      <ModalHeader>{intl.formatMessage({ defaultMessage: 'Personalize Dashboard' })}</ModalHeader>
      <ModalBody className="modal-body-personalize-dashboard">
        <div>
          <div>
            <ul>
              {/* eslint-disable jsx-a11y/click-events-have-key-events */}
              {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
              {availableComponents.map(c => (
                <li
                  key={c.slug}
                  onClick={() => handleChangeComponentSelected(c.slug)}
                  className={classnames({ selected: selectedComponents.includes(c.slug) })}
                >
                  <Input
                    type="checkbox"
                    checked={selectedComponents.includes(c.slug)}
                    className="input-check check-s"
                    readOnly={true}
                  />
                  <div className="component-details">
                    <h5>
                      {c.icon && <i className={c.icon} />}
                      {c.title}
                    </h5>
                    <p>{c.subtitle}</p>
                    {c.restrictions === 'admin' && (
                    <>
                      <div className="component-details-restriction" id={`component-details-resctriction-admin-${c.slug}`}>
                        <i className="fal fa-sparkle" />
                      </div>
                      <UncontrolledTooltip target={`component-details-resctriction-admin-${c.slug}`}>
                        {intl.formatMessage({ defaultMessage: 'Visible only to admins' })}
                      </UncontrolledTooltip>
                    </>
                    )}
                  </div>
                </li>
              ))}
              {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
              {/* eslint-enable jsx-a11y/click-events-have-key-events */}
            </ul>
          </div>
          <div ref={containerRef}>
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              width={width}
              rowHeight={50}
              height={height}
              isDraggable
              isResizable
              preventCollision={false}
              compactType="vertical"
              onLayoutChange={onLayoutChange}
            >
              {layout.map(e => {
                const newPosition = newLayout ? newLayout.find(n => n.i === e.i) : null;

                return (
                  <div key={e.i} data-grid={e} className={`component-${e.i}`}>
                    <DashboardPersonalizeElement
                      slug={e.i}
                      availableComponents={availableComponents}
                      position={newPosition || e}
                    />
                  </div>
                );
              })}
            </GridLayout>
          </div>
        </div>
        <div className="personalize-dashboard-modal-bottom-section">
          <ul>
            {/* eslint-disable jsx-a11y/click-events-have-key-events */}
            {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
            {(onboardingSteps && onboardingSteps.length > 0) && (
              <li
                onClick={() => setShowOnboarding(!showOnboarding)}
                className={classnames({ selected: showOnboarding })}
              >
                <Input
                  type="checkbox"
                  checked={showOnboarding}
                  className="input-check check-s"
                  readOnly={true}
                />
                <h5>{intl.formatMessage({ defaultMessage: 'Show Onboarding Checklist' })}</h5>
              </li>
            )}
            {/* eslint-enable jsx-a11y/click-events-have-key-events */}
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
          </ul>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={handleClose}>
          {intl.formatMessage({ defaultMessage: 'Cancel' })}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={!newLayout}>
          <ProcessingSpinner show={loading} />
          {intl.formatMessage({ defaultMessage: 'Save' })}
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  updateUserSetting: (setting) => {
    dispatch(setUserSetting(setting));
  },
});

DashboardPersonalizeModal.propTypes = {
  profile: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateUserSetting: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPersonalizeModal);
